import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Icon, Button } from 'antd';
import DropzoneS3Uploader from 'react-dropzone-s3-uploader';

import { REACT_APP_ENDPOINT } from 'config/env';
import { getPrivateDownloadUrl, getUploadSigningHeaders } from 'utils/apis/s3';

import styles from './UploadFiles.module.css';
import intl from 'react-intl-universal';

const uploadOptions = {
  server: REACT_APP_ENDPOINT,
  signingUrlQueryParams: { uploadType: 'avatar' },
  signingUrlHeaders: {},
  // Bucket has ACLs disabled (bucket owner enforced); do not send x-amz-acl.
  uploadRequestHeaders: {}
};
// Private variant: sign against the private bucket under a server-enforced key
// prefix (expenses/, billing/, ...). Uploaded files are served via presigned
// GET on click rather than a direct public URL.
const buildPrivateUploadOptions = privateType => ({
  ...uploadOptions,
  signingUrl: `/s3/private/${privateType}/sign`,
  signingUrlQueryParams: { uploadType: privateType }
});
// The stored URL is returned by the backend (info.fileUrl). s3Url is unused for
// the result but the uploader library requires the prop to be a string.
const s3Url = '';

// Open a private attachment by resolving a short-lived presigned URL on click.
// Opens the tab synchronously to avoid popup blockers, then redirects it.
const openPrivateFile = file => async e => {
  e.preventDefault();
  const win = window.open('', '_blank');
  const url = await getPrivateDownloadUrl(file.key || file.name);
  if (url) {
    if (win) win.location = url;
    else window.location = url;
  } else if (win) {
    win.close();
  }
};

// A file link that previews via presigned URL when private, or links directly
// to the stored URL for legacy/public files.
const FileLink = ({ file, isPrivate }) => {
  if (isPrivate && file.key) {
    return (
      <a href={file.link || '#'} onClick={openPrivateFile(file)} target="_blank" rel="noopener noreferrer">
        {file.name}
      </a>
    );
  }
  return (
    <a href={file.link} target="_blank" rel="noopener noreferrer">
      {file.name}
    </a>
  );
};

const UploadFiles = ({
  files,
  containerClassname,
  onUploadFinish,
  onFileDelete,
  checkIsAdmin,
  largerDragDrop = false,
  isPrivate = false,
  privateType = 'expenses'
}) => {
  // Private signing requires auth; attach the JWT for the uploader's own XHR.
  const upload = isPrivate ? { ...buildPrivateUploadOptions(privateType), signingUrlHeaders: getUploadSigningHeaders() } : uploadOptions;
  if (largerDragDrop) {
    return (
      <Row type="flex" align="middle" className={containerClassname}>
        <Col span={24}>
          <DropzoneS3Uploader
            className={styles.dashed}
            maxSize={1024 * 1024 * 10}
            onFinish={onUploadFinish}
            s3Url={s3Url}
            upload={upload}
            style={{
              width: 225,
              height: 80,
              border: '2px dashed #ccc',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 8
            }}
          >
            <Button>
              <Icon type="upload" /> {intl.get('invoice.headerLabels.clicktoUpload').d('Click to Upload Files')}
            </Button>
          </DropzoneS3Uploader>

          <div style={{ marginBottom: 12 }}>
            {files.map((file, index) => (
              <div key={file.key || file.link || index} style={{ marginBottom: 4, textAlign: 'center' }}>
                <FileLink file={file} isPrivate={isPrivate} />
                {checkIsAdmin && (
                  <span style={{ marginLeft: 8 }}>
                    <Icon onClick={onFileDelete(index)} type="close" style={{ cursor: 'pointer' }} />
                  </span>
                )}
              </div>
            ))}
          </div>
        </Col>
      </Row>
    );
  } else {
    return (
      <Row type="flex" align="middle" className={containerClassname}>
        <Col span={24} className={styles.fileContainer}>
          <DropzoneS3Uploader maxSize={1024 * 1024 * 10} onFinish={onUploadFinish} s3Url={s3Url} upload={upload} className={styles.dragBoxContainer}>
            <Button className={styles.button}>
              <Icon type="upload" /> {intl.get('invoice.headerLabels.clicktoUpload').d('Click to Upload Files')}
            </Button>
          </DropzoneS3Uploader>
          <div className={styles.fileList}>
            {files.map((file, index) => (
              <div key={file.key || file.link || index}>
                <FileLink file={file} isPrivate={isPrivate} />
                {checkIsAdmin && (
                  <span className={styles.delete}>
                    <Icon onClick={onFileDelete(index)} type="close" style={{ cursor: 'pointer' }} />
                  </span>
                )}
              </div>
            ))}
          </div>
        </Col>
      </Row>
    );
  }
};

FileLink.propTypes = {
  file: PropTypes.object.isRequired,
  isPrivate: PropTypes.bool
};

UploadFiles.propTypes = {
  onUploadFinish: PropTypes.func.isRequired,
  onFileDelete: PropTypes.func,
  checkIsAdmin: PropTypes.bool,
  containerClassname: PropTypes.string,
  files: PropTypes.array,
  // When true, sign uploads against the private bucket and resolve presigned
  // URLs on click instead of linking directly.
  isPrivate: PropTypes.bool,
  // Server-enforced key prefix bucket (expenses, billing, ...) for private mode.
  privateType: PropTypes.string
};

UploadFiles.defaultProps = {
  containerClassname: '',
  files: [],
  onUploadFinish: () => {},
  onFileDelete: () => {},
  checkIsAdmin: false,
  isPrivate: false,
  privateType: 'expenses'
};

export default UploadFiles;
